import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { formatCurrencyAs } from '../../../utils/currency';
import { getCurrentCompanyName, getCurrentCompanyAddress, getCurrentCompanyLogo, getCurrentCompanyId } from '../../../utils/auth';
import { downloadInvoicePDF } from '../../../utils/pdfGenerator';
import { customersAPI } from '../../../lib/api';

const InvoicePreviewModal = ({ isOpen, onClose, invoice }) => {
  const [customerData, setCustomerData] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Fetch customer data when invoice changes
  useEffect(() => {
    const fetchCustomerData = async () => {
      const customerId = invoice?.customerId || invoice?.CustomerId;
      if (!customerId) return;
      
      try {
        setLoadingCustomer(true);
        const companyId = getCurrentCompanyId();
        const response = await customersAPI.getAll(companyId);
        
        let customers = [];
        if (response.data?.Success && Array.isArray(response.data.Data)) {
          customers = response.data.Data;
        } else if (Array.isArray(response.data)) {
          customers = response.data;
        }
        
        const customer = customers.find(c => 
          (c.Id || c.id) === customerId
        );
        
        if (customer) {
          setCustomerData({
            companyName: customer.CompanyName || customer.companyName,
            email: customer.Email || customer.email,
            address: customer.Address || customer.address,
            city: customer.City || customer.city,
            state: customer.State || customer.state,
            zipCode: customer.ZipCode || customer.zipCode,
            country: customer.Country || customer.country,
            phone: customer.Phone || customer.phone
          });
        }
      } catch (error) {
        console.error('Error fetching customer data:', error);
      } finally {
        setLoadingCustomer(false);
      }
    };

    if (isOpen && invoice) {
      fetchCustomerData();
    }
  }, [isOpen, invoice]);

  if (!isOpen || !invoice) return null;

  // Get company information from login data
  const companyName = getCurrentCompanyName();
  const companyAddress = getCurrentCompanyAddress();
  const companyLogo = getCurrentCompanyLogo();
  const invoiceNumber = invoice?.invoiceNumber || invoice?.InvoiceNumber;
  const invoiceStatus = invoice?.status || invoice?.Status;
  const invoiceCurrency = invoice?.currency || invoice?.Currency;
  const issueDate = invoice?.issueDate || invoice?.IssueDate;
  const dueDate = invoice?.dueDate || invoice?.DueDate;
  const customerName = invoice?.customerName || invoice?.CustomerName || invoice?.customer;
  const customerEmail = invoice?.customerEmail || invoice?.CustomerEmail;
  const totalAmount = toNumber(invoice?.amount ?? invoice?.Amount);
  const paidAmount = toNumber(invoice?.paidAmount ?? invoice?.PaidAmount);
  const taxAmount = toNumber(invoice?.taxAmount ?? invoice?.TaxAmount);
  const exchangeRate = toNumber(invoice?.exchangeRate ?? invoice?.ExchangeRate);
  const rawLineItems = Array.isArray(invoice?.lineItems)
    ? invoice.lineItems
    : Array.isArray(invoice?.Items)
      ? invoice.Items
      : [];
  const lineItems = rawLineItems.map((item) => ({
    description: item?.description || item?.Description || '',
    quantity: toNumber(item?.quantity ?? item?.Quantity),
    rate: toNumber(item?.rate ?? item?.unitPrice ?? item?.UnitPrice),
    amount: toNumber(item?.amount ?? item?.Amount)
  }));
  const subtotal = toNumber(invoice?.subtotal ?? invoice?.Subtotal)
    || (lineItems.length > 0
      ? lineItems.reduce((sum, item) => sum + item.amount, 0)
      : Math.max(totalAmount - taxAmount, 0));
  const explicitVatRate = toNumber(invoice?.vatRate ?? invoice?.VatRate);
  const vatRate = explicitVatRate || (subtotal > 0 && taxAmount > 0 ? (taxAmount / subtotal) * 100 : 0);
  const displayVatRate = Number.isInteger(vatRate) ? vatRate : Number(vatRate.toFixed(2));

  const formatAmount = (amount) => {
    return formatCurrencyAs(amount, invoiceCurrency);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDownloadPDF = async () => {
    const result = await downloadInvoicePDF('invoice-content', invoiceNumber || 'invoice');
    if (!result.success) {
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">Invoice Preview</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
              iconName="Download"
              iconPosition="left"
            >
              <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              iconName="Printer"
              iconPosition="left"
            >
              <span className="hidden sm:inline">Print</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              aria-label="Close preview"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-muted/20">
          <div id="invoice-content" className="bg-background rounded-lg shadow-sm border border-border p-8 max-w-3xl mx-auto" style={{ minHeight: 'auto' }}>
            {/* Header with blue and yellow Esque colors */}
            <div className="flex items-start justify-between mb-8 pb-6 border-b-4" style={{ borderColor: '#2563EB' }}>
              <div className="flex items-center gap-4">
                {companyLogo ? (
                  <img 
                    src={companyLogo} 
                    alt={companyName}
                    className="h-16 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-md flex items-center justify-center" style={{ backgroundColor: '#2563EB' }}>
                    <Icon name="Building2" size={32} color="#FCD34D" />
                  </div>
                )}
                <div>
                  <h1 className="font-heading font-bold text-2xl" style={{ color: '#2563EB' }}>{companyName}</h1>
                </div>
              </div>

              <div className="text-right">
                <h2 className="font-heading font-bold text-3xl mb-2" style={{ color: '#FCD34D', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>INVOICE</h2>
                <div className="space-y-1">
                  <p className="data-text font-semibold text-foreground">{invoiceNumber}</p>
                  {invoiceCurrency && <p className="caption text-muted-foreground">Currency: {invoiceCurrency}</p>}
                  <InvoiceStatusBadge status={invoiceStatus} />
                </div>
              </div>
            </div>

            {/* Company Address */}
            <div className="mb-6">
              <div className="caption text-muted-foreground space-y-1">
                {companyAddress ? (
                  <>
                    {companyAddress.street && <p>{companyAddress.street}</p>}
                    {(companyAddress.city || companyAddress.state || companyAddress.zipCode) && (
                      <p>
                        {[companyAddress.city, companyAddress.state, companyAddress.zipCode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                    {companyAddress.country && <p>{companyAddress.country}</p>}
                    {companyAddress.email && <p>{companyAddress.email}</p>}
                    {companyAddress.phone && <p>{companyAddress.phone}</p>}
                  </>
                ) : (
                  <>
                    <p>Company address not available</p>
                    <p>Please update company information</p>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-border">
              <div>
                <h3 className="font-heading font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>BILL TO</h3>
                <div className="space-y-1">
                  <p className="font-body font-semibold text-foreground">{customerName}</p>
                  {customerData ? (
                    <>
                      {customerData.address && <p className="caption text-muted-foreground">{customerData.address}</p>}
                      {(customerData.city || customerData.state || customerData.zipCode) && (
                        <p className="caption text-muted-foreground">
                          {[customerData.city, customerData.state, customerData.zipCode]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}
                      {customerData.country && <p className="caption text-muted-foreground">{customerData.country}</p>}
                    </>
                  ) : loadingCustomer ? (
                    <p className="caption text-muted-foreground">Loading address...</p>
                  ) : null}
                  {customerEmail && <p className="caption text-muted-foreground">{customerEmail}</p>}
                </div>
              </div>

              <div className="md:text-right">
                <div className="space-y-2">
                  <div>
                    <span className="caption text-muted-foreground">Issue Date: </span>
                    <span className="font-body font-medium text-foreground">{formatDate(issueDate)}</span>
                  </div>
                  <div>
                    <span className="caption text-muted-foreground">Due Date: </span>
                    <span className="font-body font-medium text-foreground">{formatDate(dueDate)}</span>
                  </div>
                  <div>
                    <span className="caption text-muted-foreground">Currency: </span>
                    <span className="font-body font-medium text-foreground">{invoiceCurrency || 'N/A'}</span>
                  </div>
                  {paidAmount > 0 && (
                    <div>
                      <span className="caption text-muted-foreground">Amount Paid: </span>
                      <span className="font-body font-medium text-success">{formatAmount(paidAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: '#2563EB' }}>
                    <th className="text-left py-3 font-heading font-semibold text-sm" style={{ color: '#2563EB' }}>DESCRIPTION</th>
                    <th className="text-right py-3 font-heading font-semibold text-sm" style={{ color: '#2563EB' }}>QTY</th>
                    <th className="text-right py-3 font-heading font-semibold text-sm" style={{ color: '#2563EB' }}>RATE</th>
                    <th className="text-right py-3 font-heading font-semibold text-sm" style={{ color: '#2563EB' }}>AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length > 0 ? (
                    lineItems.map((item, index) => (
                      <tr key={index} className="border-b border-border">
                        <td className="py-4 font-body text-sm text-foreground">{item.description}</td>
                        <td className="py-4 text-right data-text text-foreground">{item.quantity}</td>
                        <td className="py-4 text-right data-text text-foreground">{formatAmount(item.rate)}</td>
                        <td className="py-4 text-right data-text font-semibold text-foreground">{formatAmount(item.amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-b border-border">
                      <td className="py-4 font-body text-sm text-muted-foreground italic" colSpan="4">
                        No line items available for this invoice
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mb-8">
              <div className="w-full md:w-80 space-y-3">
                <div className="flex items-center justify-between py-2">
                  <span className="font-body text-sm text-muted-foreground">Subtotal</span>
                  <span className="data-text font-semibold text-foreground">{formatAmount(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="font-body text-sm text-muted-foreground">VAT ({displayVatRate}%)</span>
                  <span className="data-text font-semibold text-foreground">{formatAmount(taxAmount)}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-t-2" style={{ borderColor: '#2563EB' }}>
                  <span className="font-heading font-bold text-base" style={{ color: '#2563EB' }}>Total</span>
                  <span className="data-text font-bold text-2xl" style={{ color: '#FCD34D', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>{formatAmount(totalAmount)}</span>
                </div>
                {paidAmount > 0 && (
                  <div className="flex items-center justify-between py-2 bg-success/10 px-3 rounded-md">
                    <span className="font-body font-medium text-sm text-success">Amount Due</span>
                    <span className="data-text font-bold text-lg text-success">
                      {formatAmount(totalAmount - paidAmount)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-6 border-t border-border space-y-4">
              {(invoice?.notes || invoice?.Notes) && (
                <div>
                  <h4 className="font-heading font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>Payment Terms</h4>
                  <p className="caption text-muted-foreground whitespace-pre-line">
                    {invoice?.notes || invoice?.Notes}
                  </p>
                </div>
              )}

              {(invoice?.terms || invoice?.Terms) && (
                <div>
                  <h4 className="font-heading font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>Terms & Conditions</h4>
                  <p className="caption text-muted-foreground whitespace-pre-line">
                    {invoice?.terms || invoice?.Terms}
                  </p>
                </div>
              )}

              {!(invoice?.notes || invoice?.Notes) && !(invoice?.terms || invoice?.Terms) && (
                <div>
                  <h4 className="font-heading font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>Notes</h4>
                  <p className="caption text-muted-foreground">
                    Thank you for your business. If you have any questions about this invoice, please contact us.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            variant="default"
            onClick={handleDownloadPDF}
            iconName="Download"
            iconPosition="left"
          >
            Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreviewModal;